import React, { useState } from 'react';
import { X, Mail, Lock, User, Phone, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const LoginModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState('form'); // 'form', 'verify', 'complete'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', phone: '', code: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState('');
  const { login, register } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        // Login flow (no verification needed)
        const result = await login(formData.email, formData.password);
        if (result.success) {
          onClose();
          setFormData({ email: '', password: '', name: '', phone: '', code: '' });
          setStep('form');
        } else {
          setError(result.error);
        }
      } else {
        // Registration flow with email verification
        if (step === 'form') {
          // Step 1: Send verification code
          if (!formData.name || !formData.email || !formData.phone || !formData.password) {
            setError('Tous les champs sont obligatoires');
            setLoading(false);
            return;
          }
          
          const response = await authAPI.sendVerification({
            email: formData.email,
            name: formData.name
          });
          
          if (response.data.dev_code) {
            setDevCode(response.data.dev_code);
          }
          
          setStep('verify');
          setError('');
        } else if (step === 'verify') {
          // Step 2: Verify code
          if (!formData.code || formData.code.length !== 6) {
            setError('Veuillez entrer le code à 6 chiffres');
            setLoading(false);
            return;
          }
          
          await authAPI.verifyCode({
            email: formData.email,
            code: formData.code
          });
          
          // Step 3: Complete registration
          const result = await register({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
            role: 'client',
          });
          
          if (result.success) {
            setStep('complete');
            setTimeout(() => {
              onClose();
              setFormData({ email: '', password: '', name: '', phone: '', code: '' });
              setStep('form');
            }, 2000);
          } else {
            setError(result.error);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    setStep('form');
    setError('');
    setFormData({ email: '', password: '', name: '', phone: '', code: '' });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Success State */}
        {step === 'complete' && (
          <div className="px-8 py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Inscription réussie !</h2>
            <p className="text-gray-500">Redirection en cours...</p>
          </div>
        )}

        {/* Form States */}
        {step !== 'complete' && (
          <>
            {/* Header */}
            <div className="px-8 pt-8 pb-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {mode === 'login' 
                  ? 'Connexion' 
                  : step === 'verify' 
                  ? 'Vérification email' 
                  : 'Inscription'}
              </h2>
              <p className="text-sm text-gray-500">
                {mode === 'login'
                  ? 'Connectez-vous à votre compte Khadam Express'
                  : step === 'verify'
                  ? `Code envoyé à ${formData.email}`
                  : 'Créez votre compte Khadam Express gratuitement'
                }
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-8 pb-8">
              {/* Registration Form Fields */}
              {mode === 'register' && step === 'form' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom complet *</label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={handleChange('name')}
                        placeholder="Votre nom complet"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        placeholder="votre@email.com"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Téléphone *</label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        placeholder="+212 6XX XXX XXX"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Mot de passe *</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange('password')}
                        placeholder="••••••••"
                        className="w-full pl-10 pr-12 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Verification Code Field */}
              {mode === 'register' && step === 'verify' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Code de vérification</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={handleChange('code')}
                    placeholder="123456"
                    maxLength={6}
                    className="w-full text-center text-2xl tracking-widest font-bold py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  {devCode && (
                    <p className="text-xs text-orange-600 mt-2 text-center">Mode développement - Code: {devCode}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2 text-center">Entrez le code à 6 chiffres envoyé par email</p>
                </div>
              )}

              {/* Login Fields */}
              {mode === 'login' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        placeholder="votre@email.com"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Mot de passe</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange('password')}
                        placeholder="••••••••"
                        className="w-full pl-10 pr-12 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading 
                  ? 'Chargement...' 
                  : mode === 'login' 
                  ? 'Se connecter' 
                  : step === 'verify'
                  ? 'Vérifier'
                  : 'Envoyer le code'}
              </button>

              {/* Toggle Mode */}
              {step === 'form' && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => handleModeSwitch(mode === 'login' ? 'register' : 'login')}
                    className="text-sm text-blue-500 hover:text-blue-600 font-medium"
                  >
                    {mode === 'login' 
                      ? "Pas encore de compte ? S'inscrire" 
                      : 'Déjà un compte ? Se connecter'}
                  </button>
                </div>
              )}

              {/* Back button during verification */}
              {step === 'verify' && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setStep('form')}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    ← Modifier mes informations
                  </button>
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
